import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Compass, 
  Users, 
  GraduationCap, 
  Wrench, 
  Zap, 
  BookOpen, 
  HeartHandshake, 
  Lightbulb, 
  Network, 
  Clock 
} from "lucide-react";

const BenefitsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const installedTherapists = [
    {
      icon: Compass,
      title: "Approche Holistique et Systémique",
      description: "Intervenez sur les dimensions physique, émotionnelle, mentale et spirituelle avec des connaissances quantiques intégrées.",
    },
    {
      icon: Users,
      title: "Communauté Engagée",
      description: "Rejoignez un groupe privé de professionnels partageant les mêmes valeurs pour des échanges et un soutien mutuel.",
    },
    {
      icon: GraduationCap,
      title: "Tutorat par les Anciens Élèves",
      description: "Profitez de sessions de mentorat et coaching par des thérapeutes ayant déjà suivi la formation.",
    },
    {
      icon: Wrench,
      title: "Ressources et Outils de Qualité",
      description: "Accédez à des vidéos, ebooks et outils vibratoires pour chaque module avec une progression à votre rythme.",
    },
    {
      icon: Zap,
      title: "Accélération des Résultats",
      description: "Utilisez des techniques avancées pour des transformations rapides et durables chez vos patients.",
    },
  ];

  const aspiringTherapists = [
    {
      icon: BookOpen,
      title: "Formation Complète et Accessible",
      description: "Une formation structurée pour vous lancer dans le domaine thérapeutique avec confiance et efficacité.",
    },
    {
      icon: HeartHandshake,
      title: "Accompagnement et Support",
      description: "Bénéficiez de l'accompagnement de thérapeutes expérimentés pour guider vos débuts.",
    },
    {
      icon: Lightbulb,
      title: "Développement Personnel et Professionnel",
      description: "Évoluez personnellement tout en développant une carrière enrichissante et significative.",
    },
    {
      icon: Network,
      title: "Opportunités de Réseau",
      description: "Intégrez une communauté professionnelle dès le début pour des collaborations futures.",
    },
    {
      icon: Clock,
      title: "Flexibilité et Autonomie",
      description: "Apprenez à votre rythme avec la possibilité d'adapter la formation à votre emploi du temps.",
    },
  ];

  const BenefitCard = ({ benefit, index, delay }) => {
    const Icon = benefit.icon;
    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, delay }}
        className="group"
      >
        <div className="glass-card p-6 rounded-xl h-full card-hover border-indigo-500/20">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center mb-4 group-hover:from-purple-500/30 group-hover:to-cyan-500/30 transition-all">
            <Icon className="w-6 h-6 text-cyan-400" />
          </div>
          <h4
            className="text-xl font-light text-white mb-2"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {benefit.title}
          </h4>
          <p className="text-slate-400 text-sm leading-relaxed">
            {benefit.description}
          </p>
        </div>
      </motion.div>
    );
  };

  return (
    <section
      ref={ref}
      data-testid="benefits-section"
      className="relative py-24 lg:py-32 overflow-hidden"
      style={{ background: "#030014" }}
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-cyan-400 text-sm uppercase tracking-[0.2em] mb-4 block">
            Les Avantages
          </span>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-white mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Avantages pour les <span className="text-gradient-cosmic">Thérapeutes</span>
          </h2>
        </motion.div>

        {/* For Installed Therapists */}
        <div className="mb-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-4 mb-8"
          >
            <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
              <span className="text-xl">✨</span>
            </div>
            <h3
              className="text-2xl sm:text-3xl font-light text-white"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Pour les Thérapeutes Déjà Installés
            </h3>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {installedTherapists.map((benefit, index) => (
              <BenefitCard
                key={index}
                benefit={benefit}
                index={index}
                delay={0.1 + index * 0.1}
              />
            ))}
          </div>
        </div>

        {/* For Aspiring Therapists */}
        <div>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex items-center gap-4 mb-8"
          >
            <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
              <span className="text-xl">✨</span>
            </div>
            <h3
              className="text-2xl sm:text-3xl font-light text-white"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Pour Ceux Qui Souhaitent Devenir Thérapeutes
            </h3>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {aspiringTherapists.map((benefit, index) => (
              <BenefitCard
                key={index}
                benefit={benefit}
                index={index}
                delay={0.4 + index * 0.1}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
